import React, { useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { db } from '../firebase';
import AppLinksFooter from './AppLinksFooter';
import { isMobilePlatform } from '../utils/platform';

const isOpenedFromApp = (searchParams) => {
  const from = (searchParams.get('from') || searchParams.get('source') || '').toLowerCase();
  if (from === 'app') return true;
  if (typeof window === 'undefined') return false;
  if (window.ReactNativeWebView || window.flutter_inappwebview) return true;
  if (window.webkit?.messageHandlers && !window.safari) return true;
  const ua = window.navigator.userAgent || '';
  return /WebView|(iPhone|iPod|iPad)(?!.*Safari)|Android.*; wv\)/i.test(ua);
};

const DEFAULT_CONTENT = `TERMS AND CONDITIONS

Effective Date: June 3, 2026
Last Updated: June 3, 2026

Welcome to GuffPuff! These Terms and Conditions ("Terms") govern your access to and use of the GuffPuff mobile application and related services ("Service") operated by GuffPuff LLC. By downloading, installing, or using the GuffPuff app, you agree to be bound by these Terms.

1. ACCEPTANCE OF TERMS

By accessing or using our Service, you confirm that you are at least 18 years of age, that you have read and understood these Terms, and that you agree to be legally bound by them.

2. DESCRIPTION OF SERVICE

GuffPuff is a social voice and chat platform that allows users to create and join live audio conversations, send direct messages, explore interest-based categories, and connect with other users.

3. USER ACCOUNTS

You must create an account to use GuffPuff. You agree to provide accurate, current, and complete information during registration. You are responsible for maintaining the confidentiality of your login credentials.

4. ACCEPTABLE USE POLICY

You agree NOT to use GuffPuff to post abusive, harassing, or hateful content; impersonate others; share explicit content; distribute spam; engage in illegal activity; or attempt to hack or disrupt the platform.

5. CONTENT OWNERSHIP

You retain ownership of content you create on GuffPuff. By posting content, you grant GuffPuff a non-exclusive, worldwide, royalty-free license to use and display that content solely for operating the Service.

6. SUBSCRIPTIONS AND PAYMENTS

GuffPuff offers free and premium tiers. Paid subscriptions unlock additional features and are billed monthly or annually. You may cancel anytime; access continues until the end of the billing period.

7. TERMINATION

We reserve the right to suspend or terminate your account if you violate these Terms or engage in conduct harmful to the platform or other users.

8. CONTACT US

Email: legal@guffpuff.com
Support: support@guffpuff.com
Website: www.guffpuff.com`;

const TermsAndConditionsPage = () => {
  const [content, setContent] = useState('');
  const [title, setTitle] = useState('Terms and Conditions');
  const [lastUpdated, setLastUpdated] = useState('');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const hideBack = isMobilePlatform(searchParams.toString()) || isOpenedFromApp(searchParams);

  const handleBack = () => {
    if (isOpenedFromApp(searchParams)) {
      if (window.ReactNativeWebView) {
        window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'back' }));
      }
      window.history.back();
      return;
    }
    navigate('/admin/login');
  };

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const docRef = doc(db, 'legal_content', 'terms_and_conditions');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          setTitle(data.title || 'Terms and Conditions');
          setContent(data.content || DEFAULT_CONTENT);
          if (data.lastUpdated) {
            setLastUpdated(
              new Date(data.lastUpdated.toDate ? data.lastUpdated.toDate() : data.lastUpdated).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })
            );
          }
        } else {
          setContent(DEFAULT_CONTENT);
        }
      } catch (err) {
        console.error('Error fetching Terms and Conditions:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div style={{ background: 'linear-gradient(to right, #22d3ee, #0891b2)', position: 'relative' }} className="py-12 px-6">
        <div className="max-w-4xl mx-auto text-center">
          {!hideBack && (
          <button
            onClick={handleBack}
            style={{ position: 'absolute', top: '24px', left: '24px', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', color: 'rgba(255,255,255,0.9)', fontSize: '14px', fontWeight: 500, padding: 0 }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
          )}
          <img
            src={`${process.env.PUBLIC_URL}/images/logo.png`}
            alt="GUFFPUFF"
            className="h-16 mx-auto mb-4"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <h1 className="text-3xl font-bold text-white">{title}</h1>
          {lastUpdated && (
            <p className="text-cyan-100 mt-2 text-sm">Last updated: {lastUpdated}</p>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto py-10 px-6">
        <div className="bg-white rounded-2xl shadow-sm p-8">
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="w-10 h-10 border-4 border-cyan-400 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : content ? (
            <div
              className="prose prose-gray max-w-none text-gray-700 leading-relaxed"
              style={{ whiteSpace: 'pre-wrap', lineHeight: '1.8', fontSize: '15px' }}
            >
              {content}
            </div>
          ) : (
            <p className="text-gray-400 text-center py-20">No content available yet.</p>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="text-center py-6 text-sm text-gray-400 space-y-3">
        <AppLinksFooter variant="muted" />
        <p>&copy; {new Date().getFullYear()} GuffPuff. All rights reserved.</p>
      </div>
    </div>
  );
};

export default TermsAndConditionsPage;
